import React, { useRef } from 'react';

import { useBeraReportV2 } from '@/context/BeraReportV2';

import { RevisionBanner } from './RevisionBanner';
import { InfoBanner } from './InfoBanner';
import { STRETCH_ISSUE, findStretchIssue } from './coverage';
import { hasPatchedSegment } from './utils';
import { PATCHED_BANNER, EDIT_ALL_BANNER, UNCOVERABLE_GAP_BANNER } from './constants';

import type { StretchIssue } from './coverage';

interface GapWarningParams {
	rulerIssue: StretchIssue | null;
	canAddStretch: boolean;
	isVideoUnavailable: boolean;
}

function shouldWarnUncoverableGap({ rulerIssue, canAddStretch, isVideoUnavailable }: GapWarningParams): boolean {
	if (isVideoUnavailable) {
		return false;
	}

	const hasUncoveredGap = rulerIssue === STRETCH_ISSUE.INCOMPLETE_COVERAGE;
	const result = hasUncoveredGap && !canAddStretch;

	return result;
}

export function Banners(): JSX.Element {
	const {
		allReports,
		allStretches,
		rulerStretches,
		videoDuration,
		isRevision,
		isEditMode,
		isVideoUnavailable,
		canAddStretch,
		isDraggingStretch
	} = useBeraReportV2();

	const gapWarningRef = useRef<boolean>(false);

	const isPatchedBannerShown = allReports.some(hasPatchedSegment) && !isVideoUnavailable;

	let revisionIssue: StretchIssue | null = null;

	if (isRevision) {
		revisionIssue = findStretchIssue({ stretches: allStretches, videoDuration });
	}

	const rulerIssue = findStretchIssue({ stretches: rulerStretches, videoDuration });

	const gapWarningParams = { rulerIssue, canAddStretch, isVideoUnavailable };

	if (!isDraggingStretch) {
		gapWarningRef.current = shouldWarnUncoverableGap(gapWarningParams);
	}

	const isGapUncoverable = gapWarningRef.current;

	return (
		<>
			{!!revisionIssue && <RevisionBanner issue={revisionIssue} />}
			{isGapUncoverable && <InfoBanner message={UNCOVERABLE_GAP_BANNER} />}
			{isPatchedBannerShown && <InfoBanner message={PATCHED_BANNER} />}
			{isEditMode && <InfoBanner message={EDIT_ALL_BANNER} />}
		</>
	);
}
